import dayjs from 'dayjs'
import type { MaybeRefOrGetter } from 'vue'
import { useState, useEventListener, whenever, toValue } from '#imports'

interface BaseOptions {
    refetchInput: (() => void | Promise<any>) | (() => void | Promise<any>)[]
    key: string
    /** @description Delay the refetch in minutes */
    delay?: number
}

export function useRefetchThrottle(options: BaseOptions & { useFocus: true }): void
export function useRefetchThrottle(options: BaseOptions & { useFocus: false, shouldRefetch: MaybeRefOrGetter<boolean> }): void
export function useRefetchThrottle(options: any): void {
    const { refetchInput, key, delay = 5, useFocus, shouldRefetch } = options

    const lastTriggerTimestamp = useState(`refetch:last-trigger-${key}`, () => dayjs().valueOf())

    const handleRefetch = () => {
        const now = dayjs()
        const lastTrigger = dayjs(lastTriggerTimestamp.value)

        const isAfterLastTrigger = now.diff(lastTrigger, 'millisecond') > (60_000 * delay)

        if (!isAfterLastTrigger) return
        if (useFocus && document.visibilityState !== 'visible') return

        lastTriggerTimestamp.value = now.valueOf()

        if (Array.isArray(refetchInput)) {
            refetchInput.forEach(fn => fn())

            return
        }

        if (typeof refetchInput === 'function') {
            refetchInput()
        }
    }

    if (import.meta.client) {
        if (useFocus) {
            useEventListener(document, 'visibilitychange', handleRefetch)
            useEventListener(window, 'focus', handleRefetch)
        }

        if (toValue(shouldRefetch)) {
            whenever(() => toValue(shouldRefetch), (value) => {
                if (!value) return

                handleRefetch()
            }, { immediate: true })
        }
    }
}
